var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext } from "react";
import Keycloak from "keycloak-js";
import { createContext, useEffect, useState } from "react";
import { getValueFromCookie, sleep, toBCP47, USER_LANGUAGE_COOKIE_NAME } from "@sequentech/ui-core";
import { SettingsContext } from "./SettingsContextProvider";
/**
 * Default values for the {@link AuthContext}
 */
var defaultAuthContextValues = {
    isAuthenticated: false,
    userId: "",
    username: "",
    email: "",
    firstName: "",
    logout: function () { },
    login: function (tenantId, eventId) { },
    hasRole: function () { return false; },
    getAccessToken: function () { return undefined; },
    openProfileLink: function () { return new Promise(function () { return undefined; }); },
};
/**
 * Create the AuthContext using the default values.
 */
export var AuthContext = createContext(defaultAuthContextValues);
/**
 * AuthContextProvider is responsible for managing the authentication state of the current user.
 *
 * @param props
 */
var AuthContextProvider = function (props) {
    console.log("rendering AuthContextProvider");
    var _a = useContext(SettingsContext), loaded = _a.loaded, globalSettings = _a.globalSettings;
    var _b = useState(), keycloak = _b[0], setKeycloak = _b[1];
    var _c = useState(false), isKeycloakInitialized = _c[0], setIsKeycloakInitialized = _c[1];
    // Create the local state in which we will keep track if a user is authenticated
    var _d = useState(false), isAuthenticated = _d[0], setAuthenticated = _d[1];
    // Local state that will contain the users name once it is loaded
    var _e = useState(""), userId = _e[0], setUserId = _e[1];
    var _f = useState(""), username = _f[0], setUsername = _f[1];
    var _g = useState(""), email = _g[0], setEmail = _g[1];
    var _h = useState(""), firstName = _h[0], setFirstName = _h[1];
    var _j = useState(null), tenantId = _j[0], setTenantId = _j[1];
    var _k = useState(null), eventId = _k[0], setEventId = _k[1];
    var _l = useState(undefined), defaultLocale = _l[0], setDefaultLocale = _l[1];
    var sleepSecs = 50;
    var bufferSecs = 10;
    var getLanguageFromURL = function () {
        var params = new URLSearchParams(window.location.search);
        return params.get("lang") || undefined;
    };
    var createKeycloak = function () {
        if (keycloak) {
            return;
        }
        if (!tenantId || !eventId) {
            console.log("Received empty tenant or event id, ignoring..");
            return;
        }
        /**
         * KeycloakConfig configures the connection to the Keycloak server.
         */
        var createKeycloakConfig = function (tenantId, eventId, keycloakUrl, clientId) {
            return {
                realm: "tenant-".concat(tenantId, "-event-").concat(eventId),
                url: keycloakUrl,
                clientId: clientId,
            };
        };
        var keycloakConfig = createKeycloakConfig(tenantId, eventId, globalSettings.KEYCLOAK_URL, globalSettings.ONLINE_VOTING_CLIENT_ID);
        // Create the Keycloak client instance
        var newKeycloak = new Keycloak(keycloakConfig);
        setKeycloak(newKeycloak);
    };
    var initializeKeycloak = function () { return __awaiter(void 0, void 0, void 0, function () {
        var rawLocale, keycloakInitOptions, isAuthenticatedResponse, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    console.log("initialize Keycloak");
                    if (!keycloak) {
                        console.log("CAN'T initialize Keycloak");
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, , 6]);
                    rawLocale = getLanguageFromURL() ||
                        getValueFromCookie(USER_LANGUAGE_COOKIE_NAME) ||
                        defaultLocale;
                    keycloakInitOptions = {
                        // Configure that Keycloak will check if a user is already authenticated (when
                        // opening the app or reloading the page). If not authenticated the user will
                        // be send to the login form. If already authenticated the webapp will open.
                        onLoad: "login-required",
                        checkLoginIframe: false,
                        locale: rawLocale ? toBCP47(rawLocale) : undefined,
                    };
                    return [4 /*yield*/, keycloak.init(keycloakInitOptions)
                        // If the authentication was not successfull the user is send back to the Keycloak login form
                    ];
                case 2:
                    isAuthenticatedResponse = _a.sent();
                    if (!!isAuthenticatedResponse) return [3 /*break*/, 4];
                    console.log("user is not yet authenticated. forwarding user to login.");
                    return [4 /*yield*/, keycloak.login(keycloakInitOptions)];
                case 3:
                    _a.sent();
                    _a.label = 4;
                case 4:
                    if (!keycloak.token) {
                        console.log("error authenticating user");
                        console.log("error initializing Keycloak");
                        setAuthenticated(false);
                        return [2 /*return*/];
                    }
                    // If we get here the user is authenticated and we can update the state accordingly
                    localStorage.setItem("token", keycloak.token);
                    setAuthenticated(true);
                    setTimeout(updateTokenPeriodically, 4e3);
                    console.log("user is authenticated");
                    setIsKeycloakInitialized(true);
                    return [3 /*break*/, 6];
                case 5:
                    error_1 = _a.sent();
                    console.log("error initializing Keycloak");
                    console.log(error_1);
                    setAuthenticated(false);
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        if (keycloak || !loaded) {
            return;
        }
        if (!tenantId || !eventId) {
            console.log("Received empty tenant or event id, ignoring..");
            return;
        }
        if (isAuthenticated) {
            return;
        }
        createKeycloak();
    }, [loaded, tenantId, eventId, keycloak]);
    useEffect(function () {
        if (!keycloak || isKeycloakInitialized) {
            return;
        }
        initializeKeycloak();
    }, [isKeycloakInitialized, keycloak]);
    var updateTokenPeriodically = function () { return __awaiter(void 0, void 0, void 0, function () {
        var refreshed;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!keycloak) return [3 /*break*/, 2];
                    return [4 /*yield*/, keycloak.updateToken(sleepSecs + bufferSecs)];
                case 1:
                    refreshed = _a.sent();
                    if (!keycloak.token) {
                        console.log("error updating token");
                        return [2 /*return*/];
                    }
                    if (refreshed) {
                        localStorage.setItem("token", keycloak.token);
                    }
                    _a.label = 2;
                case 2: return [4 /*yield*/, sleep(sleepSecs * 1e3)];
                case 3:
                    _a.sent();
                    updateTokenPeriodically();
                    return [2 /*return*/];
            }
        });
    }); };
    // This effect loads the users profile in order to extract the username
    useEffect(function () {
        /**
         * Load the profile for of the user from Keycloak
         */
        function loadProfile() {
            return __awaiter(this, void 0, void 0, function () {
                var profile, newTenantId, _a;
                var _b;
                return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            if (!keycloak) {
                                return [2 /*return*/];
                            }
                            _c.label = 1;
                        case 1:
                            _c.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, keycloak.loadUserProfile()];
                        case 2:
                            profile = _c.sent();
                            if (profile.id) {
                                setUserId(profile.id);
                            }
                            if (profile.email) {
                                setEmail(profile.email);
                            }
                            if (profile.firstName) {
                                setFirstName(profile.firstName);
                            }
                            if (profile.username) {
                                setUsername(profile.username);
                            }
                            newTenantId = (_b = profile === null || profile === void 0 ? void 0 : profile.attributes["tenant-id"]) === null || _b === void 0 ? void 0 : _b[0];
                            if (newTenantId) {
                                setTenantId(newTenantId);
                            }
                            return [3 /*break*/, 4];
                        case 3:
                            _a = _c.sent();
                            console.log("error trying to load the users profile");
                            return [3 /*break*/, 4];
                        case 4: return [2 /*return*/];
                    }
                });
            });
        }
        // Only load the profile if a user is authenticated
        if (isAuthenticated) {
            loadProfile();
        }
    }, [isAuthenticated, keycloak]);
    /**
     * Initiate the logout
     */
    var logout = function () {
        if (!keycloak) {
            return;
        }
        localStorage.removeItem("token");
        keycloak.logout();
    };
    var login = function (tenantId, eventId) {
        setTenantId(tenantId);
        setEventId(eventId);
        setDefaultLocale(defaultLocale || undefined);
    };
    /**
     * Check if the user has the given role
     * @param role to be checked
     * @returns whether or not if the user has the role
     */
    var hasRole = function (role) {
        if (!keycloak) {
            return false;
        }
        return keycloak.hasRealmRole(role);
    };
    var getAccessToken = function () { return keycloak === null || keycloak === void 0 ? void 0 : keycloak.token; };
    var openProfileLink = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!keycloak) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, keycloak.accountManagement()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    // Setup the context provider
    return (_jsx(AuthContext.Provider, { value: {
            isAuthenticated: isAuthenticated,
            userId: userId,
            username: username,
            email: email,
            firstName: firstName,
            logout: logout,
            login: login,
            hasRole: hasRole,
            getAccessToken: getAccessToken,
            openProfileLink: openProfileLink,
        }, children: props.children }));
};
export default AuthContextProvider;
